import { Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getIfExistThread } from 'helpers';

import { getUserId } from 'store/selectors/user';

import { SOCKET_THREAD_TYPES } from 'settings/constants/sockets';
import { resetFormDetailsEffect } from 'store/effects/formProcess';
import { DRAWER_MESSAGES_TYPES } from 'settings/constants/drawers';

import { getThreadsListSelector } from 'store/selectors/sockets/threads';
import { FormsTableStatus } from 'app-constants';
import { Modal } from 'components-antd';
import { getFormDetailsSelector } from 'store/selectors/requestFormProcess';

import {
  changeMessagesDrawerTypeAction,
  openMessagesDrawerAction,
} from 'store/actions/drawers/messages';

import styles from './styles.module.scss';

import classNames from 'classnames';

import { FormDetailHeader } from './components';

import { isExternalMember } from '../FormsTable/helper';
import { MessageDataProps } from './components/FormDetailTabs/TrackingTab/components/FormUserDropdown';
import { getAgentTeamRoleSelector } from 'store/selectors/agentTeamDetail';
import { TEAM_ADMIN, TEAM_OWNER } from 'settings/constants/roles';

import { Spinner } from 'components';
import { FormLinkWrapper } from './components/FormLinkWrapper';
import { FormDetailActions } from './components/FormDetailActions';

import { FormDetailTabs } from './components/FormDetailTabs';
import { FormDetailModalProps } from './types';
import {
  ModalLeftSection,
  ModalRightSection,
  ModalSectionTabsWrapper,
} from 'pages/Workshop/components/ModalSections/components';
import { ModalSections } from 'pages/Workshop/components/ModalSections';

export const FormDetailModal = ({
  handleSendMessage,
  onCloseModal,
  handleViewForm,
  handleResendForm,
  handleRemoveEditor,
  handleReplaceEntity,
  viewOnly: formViewOnly,
  selectedFormProcess,
  selectedFormStatus,
  fetchFormDetails,
  onDeleteFormSuccess,
  inProgressForm,
  refetchForms,
  clientProfileId,
}: FormDetailModalProps) => {
  const dispatch = useDispatch();

  const currentUserId = useSelector(getUserId);
  const agentRole = useSelector(getAgentTeamRoleSelector);
  const isAdminOrOwner = [TEAM_ADMIN, TEAM_OWNER].includes(agentRole);
  const isTeamOwner = agentRole === TEAM_OWNER;
  const threads = useSelector(getThreadsListSelector);
  const { isPending, data: formDetails } = useSelector(getFormDetailsSelector);

  const [disableSections, setDisableSections] = useState(false);
  const [isMobile, setIsMobile] = useState(window.outerWidth <= 768);

  const isFormCreator = formDetails?.isOwner;
  const isExternalParticipant = isExternalMember(formDetails);
  let viewOnly = formViewOnly || isExternalParticipant;

  const isDraftForm = selectedFormStatus === FormsTableStatus.Draft;

  const isWaitingOnOtherForm = selectedFormStatus === FormsTableStatus.WaitingOnOthers;
  const isCompletedForm = selectedFormStatus === FormsTableStatus.Complete;

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.outerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(
    () => () => {
      resetFormDetailsEffect(dispatch);
    },
    [],
  );

  const handleMessage = ({ id, name, avatar }: MessageDataProps) => {
    const thread = getIfExistThread(
      threads,
      [id, currentUserId],
      ({ Type }) => Type === SOCKET_THREAD_TYPES.CHAT,
    );

    onCloseModal();
    dispatch(openMessagesDrawerAction(true));

    if (!thread) {
      dispatch(
        changeMessagesDrawerTypeAction({
          type: DRAWER_MESSAGES_TYPES.NEW_MESSAGE,
          params: {
            threadId: thread?.Id || null,
            participants: [
              {
                id,
                name,
                value: id,
                avatarUrl: avatar,
              },
            ],
          },
        }),
      );
    } else {
      dispatch(
        changeMessagesDrawerTypeAction({
          type: DRAWER_MESSAGES_TYPES.CHAT,
          params: {
            threadId: thread.Id,
          },
        }),
      );
    }
  };

  const renderDetailActionButtons = () => {
    return (
      <FormDetailActions
        isTeamOwner={isTeamOwner}
        formDetails={formDetails}
        refetchForms={refetchForms}
        handleViewForm={handleViewForm}
        inProgressForm={inProgressForm}
        disableSections={disableSections}
        refetchFormDetails={fetchFormDetails}
        selectedFormStatus={selectedFormStatus}
        selectedFormProcess={selectedFormProcess}
        onDeleteFormSuccess={onDeleteFormSuccess}
      />
    );
  };

  return (
    <>
      <Modal
        open={true}
        footer={null}
        width={1000}
        className={classNames(styles.formDetailModal, {
          [styles.modalLoading]: isPending,
        })}
        maskClosable={false}
        destroyOnClose={true}
        closable={false}
      >
        {isPending ? (
          <Spinner />
        ) : (
          <Fragment>
            <FormDetailHeader
              formDetails={formDetails}
              selectedFormStatus={selectedFormStatus}
              isFormCreator={isFormCreator}
              showMegaPhone={!viewOnly && !disableSections}
              inProgressForm={inProgressForm}
              handleSendMessage={handleSendMessage}
              onClose={onCloseModal}
              isDraftForm={isDraftForm}
            />

            <ModalSections className={styles.formDetailSections}>
              <ModalLeftSection>
                <div className={styles.formLeftSectionWrapper}>
                  <FormLinkWrapper
                    viewOnly={viewOnly}
                    formDetails={formDetails}
                    onCloseModal={onCloseModal}
                    disableSections={disableSections}
                    clientProfileId={clientProfileId}
                    formProcessId={selectedFormProcess}
                    refetchFormDetails={fetchFormDetails}
                    setDisableSections={setDisableSections}
                  />

                  {!isMobile && renderDetailActionButtons()}
                </div>
              </ModalLeftSection>

              <ModalRightSection>
                <ModalSectionTabsWrapper>
                  <FormDetailTabs
                    viewOnly={viewOnly}
                    isDraftForm={isDraftForm}
                    formDetails={formDetails}
                    handleMessage={handleMessage}
                    isAdminOrOwner={isAdminOrOwner}
                    handleViewForm={handleViewForm}
                    disableSections={disableSections}
                    handleResendForm={handleResendForm}
                    selectedFormStatus={selectedFormStatus}
                    handleRemoveEditor={handleRemoveEditor}
                    handleReplaceEntity={handleReplaceEntity}
                    selectedFormProcess={selectedFormProcess}
                  />
                </ModalSectionTabsWrapper>
              </ModalRightSection>
            </ModalSections>

            {isMobile && renderDetailActionButtons()}
          </Fragment>
        )}
      </Modal>
    </>
  );
};
